/* eslint-disable react/require-default-props */
import classnames from 'classnames';
import 'core-js/proposals/string-match-all';
import { generateConditionalArray } from 'helpers/array';
import { BEMBlock } from 'helpers/classnames';
import { useContext } from 'react';
import * as React from 'react';
import { ConfigContext } from 'palette/Config';
import {
    TYPES, WEIGHTS, DEFAULT_TAG_NAME, TagName,
    Weight, HeaderLevel, Color,
    HEADER_WEIGHTS,
} from './constants';
import { TextHighlighterContext } from './TextHighlighter';
import './styles/Typography.scss';
import {
    BodyProps, SmallProps, GenericProps, HeaderProps, SpanProps, Substring,
} from './types';

type TypographyProps = React.PropsWithChildren<(
    SpanProps | SmallProps | BodyProps | HeaderProps
) & GenericProps>;

function Typography({
    id: elementId,
    type = 'span',
    color,
    htmlFor,
    weight,
    level,
    transform,
    overflowType,
    textDecoration,
    underlineOffset = 'auto',
    isStrikeTrough = false,
    tagName = 'span',
    className,
    children,
    fontStyle,
}: TypographyProps) {
    const { device } = useContext(ConfigContext);
    const {
        color: highlightColor,
        weight: highlightWeight,
        value: highlightValue,
        isRegex,
    } = useContext(TextHighlighterContext);
    const pattern = isRegex ? highlightValue : highlightValue?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const isHighlighting = highlightColor ?? highlightWeight ?? pattern ?? false;

    const getDefaultWeight = () => {
        if (type === 'span') return weight;

        return type === 'header' && level ? HEADER_WEIGHTS[level] : WEIGHTS.regular;
    };

    const getClasses = (
        actualColor?: Color,
        actualWeight?: Weight,
    ) => {
        const currentColor: Color | undefined = actualColor ?? (
            type === TYPES.span ? color : 'regular');
        const currentWeight = actualWeight ?? getDefaultWeight();
        const currentSmallLevel = type === TYPES.small ? level || '1' : null;
        const currentBodyLevel = type === TYPES.body ? level || '2' : null;

        const [block] = BEMBlock('typography', {
            [type]: type && level === undefined,
            [`h${level}`]: type === 'header' && level,
            [`small${currentSmallLevel}`]: currentSmallLevel,
            [`body${currentBodyLevel}`]: currentBodyLevel,
            [`${overflowType}`]: overflowType,
            [`color-${currentColor}`]: currentColor,
            [`weight-${currentWeight}`]: currentWeight,
            [`transform-${transform}`]: transform,
            [`${device}`]: device === 'mobile' && type !== 'span',
            [`${textDecoration}`]: textDecoration,
            [`underline-offset-${underlineOffset}`]: textDecoration,
            [`style-${fontStyle}`]: fontStyle,
        });

        return classnames(className, block);
    };

    const getTagName = (): TagName => {
        if (isStrikeTrough) return 's';
        if (tagName) return tagName;
        if (type === TYPES.header && level) return `h${level as HeaderLevel}` as const;
        if (type !== undefined && type !== TYPES.header) { return DEFAULT_TAG_NAME[type]; }

        return 'span';
    };

    const generateSubStrings = (target: string) => [...target.matchAll(new RegExp(pattern!, 'gi'))].reduce<Substring[]>((
        result,
        match,
        index,
        matches,
    ) => {
        const nextMatch = matches[index + 1];
        const { index: startingIndex } = match;
        const { index: nextEndingIndex } = nextMatch ?? {};
        const endingIndex = startingIndex! + match[0].length;
        const nextStartingIndex = endingIndex;
        const isFirstMatch = index === 0;

        return generateConditionalArray<Substring>([
            {
                condition: isFirstMatch && startingIndex !== undefined && startingIndex > 0,
                entries: [{
                    id: 0,
                    value: target.substring(0, startingIndex),
                    isHighlighted: false,
                }],
            },
            {
                entries: [
                    ...result,
                    {
                        id: startingIndex!,
                        value: target.substring(startingIndex!, endingIndex),
                        isHighlighted: true,
                    },
                ],
            },
            {
                condition: nextStartingIndex !== undefined && nextStartingIndex < target.length,
                entries: [{
                    id: nextStartingIndex,
                    value: target.substring(
                        nextStartingIndex, nextEndingIndex ?? target.length,
                    ),
                    isHighlighted: false,
                }],
            },
        ]);
    }, []);
    const props = {
        htmlFor,
        className: getClasses(color, weight),
        id: elementId,
    } as Record<string, string>;

    if (isHighlighting) {
        props.translate = 'no';
    }

    return React.createElement(
        getTagName(),
        props,
        isHighlighting ? (
            <>
                {React.Children.map(children, (child) => {
                    if (typeof child !== 'string') return child;

                    const highlightMatches = generateSubStrings(child);
                    const hasMatches = highlightMatches.length > 0;
                    const highlightClasses = getClasses(highlightColor, highlightWeight);

                    if (hasMatches) {
                        return highlightMatches.map(
                            ({ id, value: substring, isHighlighted }) => (
                                isHighlighted ? (
                                    <mark
                                        key={id}
                                        className={highlightClasses}
                                    >
                                        {substring}
                                    </mark>
                                ) : substring
                            ),
                        );
                    }

                    return child;
                })}
            </>
        ) : children,
    );
}

export default Typography;

export type { TypographyProps };
